import { Component, OnDestroy, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { Observable, PartialObserver, Subscription, EMPTY, interval } from "rxjs";
import { catchError, exhaustMap, startWith, switchMap, takeWhile, tap } from "rxjs/operators";
import { FaxDirection } from "src/app/enums/fax-direction";
import { FaxStatus } from "src/app/enums/fax-status";
import { ModalName } from "src/app/enums/modal-name";
import { ToastType } from "src/app/enums/toast-type";
import { IContact } from "src/app/models/contact/contact";
import { IFax } from "src/app/models/fax/fax";
import { IGenerateFaxPdfPayload } from "src/app/models/fax/generate-fax-pdf-payload";
import { ISendTelnyxFaxSearchParams } from "src/app/models/telnyx/send-telnyx-fax-search-params";
import { ClientService } from "src/app/services/client.service";
import { FaxService } from "src/app/services/fax.service";
import { LoadingService } from "src/app/services/loading.service";
import { ModalService } from "src/app/services/modal.service";
import { NotificationService } from "src/app/services/notification.service";
import { TelnyxService } from "src/app/services/telnyx.service";
import { ISendTelnyxFaxParams } from "../../../../models/telnyx/send-telnyx-fax-params";

const FAX_STATUS_POLL_INTERVAL_MS = 30_000;

@Component({
  selector: "app-send",
  templateUrl: "./send.component.html",
  styleUrls: ["./send.component.scss"],
})
export class SendComponent implements OnInit, OnDestroy {
  readonly STEPS = ["1. Recipient info", "2. Attachment", "3. Preview Fax", "4. Send Fax"];

  currentStep = this.STEPS[0];

  faxPdfPayload: IGenerateFaxPdfPayload = {
    toNumber: "",
    fromNumber: "",
    deliveryTo: "",
    sentFrom: "",
    generateCoverPage: false,
    faxSubject: "",
    notes: "",
    files: [],
  };
  faxPdf: Blob | null = null;

  defaultRecipientData: IGenerateFaxPdfPayload | null = null;

  errorMessage = "";
  showRetryStep = false;

  showProcessingStep = false;
  sentFax: IFax | null = null;
  private _lastFaxIdBeforeSend = 0;
  private _lastSendInfo: ISendTelnyxFaxSearchParams | null = null;
  private _faxStatusPollSubscription: Subscription | null = null;

  constructor(
    private readonly _clientService: ClientService,
    private readonly _faxService: FaxService,
    private readonly _telnyxService: TelnyxService,
    private readonly _modalService: ModalService,
    private readonly _loadingService: LoadingService,
    private readonly _notificationService: NotificationService,
    private readonly _router: Router
  ) {}

  get isSentFaxPending(): boolean {
    return !this.isSentFaxSuccessful && !this.isSentFaxFailed;
  }

  get isSentFaxSuccessful(): boolean {
    return this.sentFax?.status === FaxStatus.Success;
  }

  get isSentFaxFailed(): boolean {
    return this.sentFax?.status === FaxStatus.Failed;
  }

  isActiveStep(index: number) {
    return !this.showRetryStep && this.currentStep === this.STEPS[index];
  }

  navigateToPreviousPage() {
    this.currentStep = this.STEPS[this.STEPS.indexOf(this.currentStep) - 1];
  }

  navigateToNextPage() {
    this.currentStep = this.STEPS[this.STEPS.indexOf(this.currentStep) + 1];
  }

  setRecipientInfo(data: IGenerateFaxPdfPayload) {
    this.faxPdfPayload = { ...data, files: this.faxPdfPayload.files };
    this.navigateToNextPage();
  }

  generateFaxPdf(files: File[]) {
    this.showRetryStep = false;
    this.faxPdfPayload.files = files;

    this.showLoaderWhileObserve(this._faxService.generatePdf(this.faxPdfPayload)).subscribe({
      next: faxPdf => {
        this.faxPdf = faxPdf;
        this.navigateToNextPage();
      },
      error: error => {
        console.log(error);
        this.errorMessage = error?.toString?.() ?? "An unknown error occurred";
        this.showRetryStep = true;
      },
    });
  }

  sendFax(sendInfo: ISendTelnyxFaxSearchParams) {
    this.showRetryStep = false;
    this._lastSendInfo = sendInfo;

    if (this.faxPdf === null) {
      return;
    }

    const params: ISendTelnyxFaxParams = {
      sendCopies: sendInfo.sendCopies,
      toNumber: this.faxPdfPayload.toNumber,
      fromNumber: this.faxPdfPayload.fromNumber,
      folderName: sendInfo.folderName,
      emails: sendInfo.emails,
    };

    const pdf = this.faxPdf;
    // Snapshot before sending: API timestamps do not consistently include a timezone.
    const sendRequest = this.getRecentOutboundFaxes().pipe(
      tap(response => {
        this._lastFaxIdBeforeSend = Math.max(0, ...response.records.map(fax => fax.id));
      }),
      switchMap(() => this._telnyxService.sendFax(params, pdf))
    );

    this.showLoaderWhileObserve(sendRequest).subscribe({
      next: () => {
        this.showProcessingStep = true;
        this.startPollingFaxStatus();
      },
      error: error => {
        console.log(error);
        this.errorMessage = error?.toString?.() ?? "An unknown error occurred";
        this.showRetryStep = true;
      },
    });
  }

  sendNewFax() {
    this._faxStatusPollSubscription?.unsubscribe();

    this.currentStep = this.STEPS[0];
    this.showProcessingStep = false;
    this.showRetryStep = false;
    this.sentFax = null;
    this.faxPdf = null;
    this.faxPdfPayload = {
      toNumber: "",
      fromNumber: "",
      deliveryTo: "",
      sentFrom: "",
      generateCoverPage: false,
      faxSubject: "",
      notes: "",
      files: [],
    };
  }

  private getRecentOutboundFaxes() {
    return this._faxService.getAll({
      pageNumber: "1",
      pageSize: "5",
      direction: FaxDirection.Outbound,
    });
  }

  private startPollingFaxStatus() {
    this.sentFax = null;

    this._faxStatusPollSubscription?.unsubscribe();

    this._faxStatusPollSubscription = interval(FAX_STATUS_POLL_INTERVAL_MS)
      .pipe(
        startWith(0),
        exhaustMap(() =>
          this.getRecentOutboundFaxes().pipe(
            // A failed status request must not terminate the polling timer.
            catchError(() => EMPTY)
          )
        ),
        tap(response => {
          const matchingFax = this.sentFax
            ? response.records.find(fax => fax.id === this.sentFax!.id)
            : response.records
                .filter(fax =>
                  fax.toNumber === this.faxPdfPayload.toNumber &&
                  fax.fromNumber === this.faxPdfPayload.fromNumber &&
                  fax.id > this._lastFaxIdBeforeSend
                )
                .sort((a, b) => a.id - b.id)[0];

          if (matchingFax) {
            this.sentFax = matchingFax;
          }
        }),
        takeWhile(() => this.isSentFaxPending)
      )
      .subscribe();
  }

  retrySendFax() {
    if (this.currentStep === this.STEPS[1]) {
      this.showRetryStep = false;
      return;
    }

    if (this.currentStep === this.STEPS[3] && this.faxPdf) {
      this.showRetryStep = false;

      this.sendFax(this._lastSendInfo ?? {
        sendCopies: "false",
        generateEmailBody: "false",
        toEmail: "",
        toNumber: this.faxPdfPayload.toNumber,
        fromNumber: this.faxPdfPayload.fromNumber,
        deliveryTo: this.faxPdfPayload.deliveryTo ?? "",
        sentFrom: this.faxPdfPayload.sentFrom ?? "",
        faxSubject: this.faxPdfPayload.faxSubject ?? "",
        notes: this.faxPdfPayload.notes ?? "",
        folderName: "",
      });
    }
  }

  private showLoaderWhileObserve<T>(observable: Observable<T>) {
    this._loadingService.setLoading(true);

    return {
      subscribe: (observer?: PartialObserver<T>) => {
        return observable.subscribe({
          next: result => observer?.next?.(result),
          error: error => {
            this._loadingService.setLoading(false);
            this._notificationService.callNotificationBar(error.message, ToastType.Error);
            observer?.error?.(error);
          },
          complete: () => {
            this._loadingService.setLoading(false);
            observer?.complete?.();
          },
        });
      },
    };
  }

  redirectToSubscription() {
    this._router.navigate(["/subscription"]);
  }

  ngOnInit() {
    this._clientService.getClientFlags().subscribe(clientFlags => {
      if (!clientFlags.hasSubscription) {
        this._modalService.openModal(ModalName.ExpiredSubscriptionError);
      } else if (!clientFlags.hasFaxPhoneNumber &&
        this._modalService.openedModalName !== ModalName.TrialSubscription) {
        this._modalService.openModal(ModalName.MissingSubscriptionError);
      }
    });

    const contact = history.state?.contact as IContact | undefined;

    if (contact) {
      this.defaultRecipientData = {
        toNumber: contact.faxNumber ?? "",
        deliveryTo: contact.name ?? "",
        fromNumber: "",
        generateCoverPage: false,
        files: [],
      };
    }
  }

  ngOnDestroy() {
    this._faxStatusPollSubscription?.unsubscribe();
  }
}
